import type { ReactNode } from 'react'
import type { ThemedPage } from '@/types'

import { Seo } from '@/components/generals/seo'
import FadeIn from '@/components/generals/fade-in'
import LineReveal from '@/components/generals/line-reveal'
import TextReveal from '@/components/generals/text-reveal'
import Faq from '@/components/landing/reserve/faq'
import DarkMap from '@/components/landing/reserve/dark-map'
import ReservationForm from '@/components/landing/reserve/reservation-form'
import { FAQ } from '@/constants/reserve'
import { siteConfig } from '@/constants/site'

const Reserve: ThemedPage = (): ReactNode => {
    const { contact, hours } = siteConfig

    return (
        <>
            <Seo
                title="Reserve and contact"
                description="Request a table at L’Élite in Marylebone. Opening hours, address, phone and answers to common questions: halal, zero-proof drinks, children and walk-ins."
            />

            <section id='top' aria-labelledby='reserve-title' className='relative overflow-hidden pb-24 pt-32 md:pb-36 md:pt-44'>
                <div
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-0 bg-[radial-gradient(55%_50%_at_10%_10%,rgb(201_171_124/0.08),transparent_70%)]'
                />

                <div className='shell relative grid gap-14 lg:grid-cols-12 lg:gap-x-12 lg:gap-y-16'>
                    <div className='lg:col-span-5 lg:row-start-1'>
                        <FadeIn trigger='mount' delay={0.15}>
                            <p className='caps flex items-center gap-4 text-brass'>
                                <span>Chapitre VII</span>
                            </p>
                        </FadeIn>

                        <LineReveal
                            as='h1'
                            id='reserve-title'
                            trigger='mount'
                            delay={0.3}
                            lines={["Join", <em key='us'>us.</em>]}
                            className='mt-8 font-heading text-[clamp(4.5rem,12vw,11rem)] font-normal leading-[0.84] tracking-[-0.04em]'
                        />

                        <TextReveal
                            trigger='mount'
                            delay={0.7}
                            text="Send a request and we will call to confirm. For tonight, the counter is kept for walk-ins."
                            className='mt-10 max-w-md text-lg leading-relaxed text-ivory/70'
                        />
                    </div>

                    <div className='lg:col-span-6 lg:col-start-7 lg:row-span-2 lg:row-start-1'>
                        <FadeIn trigger='mount' delay={0.45} className='lg:sticky lg:top-28'>
                            <div className='border border-ivory/10 bg-charcoal-2/60 p-6 sm:p-8 md:p-10'>
                                <div className='mb-10 flex items-baseline justify-between gap-6 border-b border-ivory/10 pb-6'>
                                    <p className='font-heading text-[1.9rem] leading-none tracking-[-0.01em]'>Request a table</p>
                                    <p className='caps text-ivory/40'>1 to 12 guests</p>
                                </div>

                                <ReservationForm />
                            </div>
                        </FadeIn>
                    </div>

                    <div className='lg:col-span-5 lg:row-start-2'>
                        <FadeIn delay={0.1}>
                            <h2 className='caps text-ivory/45'>Opening hours</h2>

                            <table className='mt-5 w-full border-t border-ivory/10 text-left'>
                                <caption className='sr-only'>Opening hours</caption>

                                <tbody>
                                    {hours.map(row => (
                                        <tr key={row.days} className='border-b border-ivory/10'>
                                            <th scope='row' className='py-4 pr-6 font-heading text-[1.35rem] font-normal md:text-[1.5rem]'>
                                                {row.daysLong}
                                            </th>
                                            <td className='py-4 text-right tabular-nums text-ivory/80'>
                                                {row.open} – {row.close}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <p className='mt-4 text-sm leading-relaxed text-ivory/45'>
                                Last seating ninety minutes before we close.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.15} className='mt-12 grid gap-8 sm:grid-cols-2'>
                            <div>
                                <h2 className='caps text-ivory/45'>Address</h2>
                                <address className='mt-4 not-italic leading-relaxed text-ivory/85'>
                                    {contact.street}<br />
                                    {contact.area}<br />
                                    {contact.city} {contact.postcode}
                                </address>
                            </div>

                            <div>
                                <h2 className='caps text-ivory/45'>Telephone</h2>
                                <a href={contact.phoneHref} className='mt-4 block tabular-nums text-ivory/85 transition-colors duration-500 hover:text-brass-bright'>
                                    {contact.phone}
                                </a>
                                <a href={contact.instagram} target='_blank' rel='noopener noreferrer' className='mt-2 block text-ivory/60 transition-colors duration-500 hover:text-brass-bright'>
                                    {contact.instagramHandle}
                                </a>
                            </div>
                        </FadeIn>

                        <FadeIn delay={0.2} className='mt-12'>
                            <DarkMap />
                        </FadeIn>
                    </div>
                </div>
            </section>

            <section id='faq' aria-labelledby='faq-title' className='border-t border-ivory/10 py-28 md:py-40'>
                <div className='shell grid gap-14 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <p className='caps flex items-center gap-4 text-brass'>
                            <span>Before you come</span>
                        </p>

                        <LineReveal
                            as='h2'
                            id='faq-title'
                            lines={["A few", <em key='questions'>questions.</em>]}
                            className='mt-8 font-heading text-[clamp(2.6rem,5vw,4.75rem)] leading-[0.96] tracking-[-0.03em]'
                        />
                    </div>

                    <div className='lg:col-span-7 lg:col-start-6'>
                        <Faq items={FAQ} />
                    </div>
                </div>
            </section>
        </>
    )
}

export default Reserve
